import { useState } from "react";
import clsx from "clsx";
import { z } from "zod";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { usePartJSON } from "@hsl/fund-page/hooks";

import useExplorerStore from "../../../store/store";
import { SimplePartProps, SimplePartSchema } from "../../../types";

export default function SubscriptionStickyBanner({
    partId,
    audienceId,
    version = "live",
    languageId,
    className,
    style,
}: SimplePartProps & { className?: string; style?: React.CSSProperties }) {
    const usertypeId = useExplorerStore((state) => state.config.usertypeId);
    const isPrivate = [64, 65, 133, 149, 150, 151].includes(usertypeId);
    const { buttonClickEvent } = useYardActionEvents();
    const [hidden, setHidden] = useState(false);
    const { data } = usePartJSON(
        true,
        {
            partId: `${partId}`,
            audienceId: `${audienceId}`,
            version,
            languageId: `${languageId}`,
        },
        SimplePartSchema,
    );
    const token = useFCSubscriptionStore((store) => store.token);
    if (token && token.length > 0) {
        return null;
    }
    if (!data || hidden || isPrivate) {
        return null;
    }
    return (
        <div
            className={clsx(
                "bg-core-blue fixed",
                "z-50 min-w-[181px] -translate-y-full",
                "rounded-[3px] p-4 text-white shadow-[0px_2px_6px_0px_rgba(0,0,0,0.16)]",
                className,
            )}
            style={style}
        >
            <div className="flex items-center gap-x-2">
                <div className="grid h-[35px] w-[35px] place-content-center rounded-full bg-white">
                    <BellIcon />
                </div>
                <p className="max-w-[135px] text-[16px] font-medium leading-[18px]">
                    {data?.title}
                </p>
            </div>
            {data?.text && (
                <div
                    className="mt-2 w-[180px] text-xs leading-4"
                    dangerouslySetInnerHTML={{ __html: data.text }}
                ></div>
            )}
            {data.routes &&
                data.routes.map((route, i) => (
                    <div key={`route-${i}`} className="text-right leading-none">
                        <a
                            href={route.url}
                            target={route.new_window ? "_blank" : "_self"}
                            className="text-xs font-medium leading-[18px] underline"
                            onClick={() =>
                                buttonClickEvent({
                                    name: "Subscription promo – need help",
                                })
                            }
                        >
                            {route.link_text}
                        </a>
                    </div>
                ))}
            <div
                className="absolute right-2 top-2 cursor-pointer"
                onClick={() => {
                    setHidden(true);
                    buttonClickEvent({ name: "Subscription promo - close" });
                }}
            >
                <XIcon />
            </div>
        </div>
    );
}

const BellIcon = () => (
    <svg
        width="24"
        height="27"
        viewBox="0 0 24 27"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_1209_3068)">
            <path
                d="M19.6667 8.2961C19.6667 3.99525 16.2294 0.5 12 0.5C7.77056 0.5 4.33333 3.99525 4.33333 8.2961C4.33333 17.3916 0.5 19.9903 0.5 19.9903H23.5C23.5 19.9903 19.6667 17.3916 19.6667 8.2961Z"
                fill="#0076D6"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.5306 21.6873C14.2774 22.8696 13.2406 23.7551 12 23.7551C10.7594 23.7551 9.72252 22.8696 9.46929 21.6873H11.1695C11.3499 21.9739 11.6628 22.1551 12 22.1551C12.3371 22.1551 12.65 21.9739 12.8304 21.6873H14.5306Z"
                fill="#0076D6"
            />
        </g>
        <defs>
            <clipPath id="clip0_1209_3068">
                <rect
                    width="23"
                    height="26"
                    fill="white"
                    transform="matrix(-1 0 0 1 23.5 0.5)"
                />
            </clipPath>
        </defs>
    </svg>
);

const XIcon = () => (
    <svg
        width="13"
        height="12"
        viewBox="0 0 13 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            opacity="0.4"
            d="M12.5 1.05L11.45 0L6.5 4.95L1.55 0L0.5 1.05L5.45 6L0.5 10.95L1.55 12L6.5 7.05L11.45 12L12.5 10.95L7.55 6L12.5 1.05Z"
            fill="white"
        />
    </svg>
);
